import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import SearchableSelect from "../../components/common/SearchableSelect";
import { errorAlert, successAlert, warningAlert } from "../../utils/alert";

const AGE_OPTIONS = [
  { value: "all", label: "All Age Categories" },
  { value: "sub_junior", label: "Sub-Junior" },
  { value: "junior", label: "Junior" },
  { value: "senior", label: "Senior" },
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const normalizeGender = (value) => {
  const v = String(value || "").toLowerCase().trim();
  if (v === "male" || v === "men") return "male";
  if (v === "female" || v === "women") return "female";
  return "";
};

const normalizeAgeCategory = (value) => {
  const v = String(value || "").toLowerCase().trim();
  if (v === "all") return "all";
  if (v === "sub_junior" || v === "sub-junior" || v === "sub junior") return "sub_junior";
  if (v === "junior") return "junior";
  if (v === "senior") return "senior";
  return "";
};

const createEmptyRule = () => ({
  id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  gender: "male",
  age_category: "sub_junior",
  sport: "",
  event: "",
  eligible_classes: "",
});

const splitClasses = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const ClassificationMultiSelect = ({ options = [], value, onChange, disabled = false }) => {
  const selected = splitClasses(value);
  const available = Array.from(new Set(options.map((item) => String(item || "").trim()).filter(Boolean)));
  const displayOptions = Array.from(new Set([...available, ...selected]));
  const selectedSet = new Set(selected);
  const allSelected = available.length > 0 && available.every((item) => selectedSet.has(item));

  const toggle = (classification) => {
    const next = selectedSet.has(classification)
      ? selected.filter((item) => item !== classification)
      : [...selected, classification];
    onChange(next.join(","));
  };

  return (
    <details className="relative" onClick={(event) => event.stopPropagation()}>
      <summary
        className={`list-none min-h-[34px] border border-gray-300 rounded px-2 py-1 cursor-pointer bg-white flex items-center justify-between gap-2 ${
          disabled ? "opacity-60 cursor-not-allowed bg-gray-100" : ""
        }`}
        onClick={(event) => {
          if (disabled) event.preventDefault();
        }}
      >
        <span className="truncate text-xs">
          {disabled
            ? "Select a sport first"
            : selected.length
              ? `${selected.length} class${selected.length === 1 ? "" : "es"} selected`
              : "Select eligible classes"}
        </span>
        <span aria-hidden="true">▾</span>
      </summary>

      {!disabled && (
        <div className="absolute z-50 mt-1 w-[320px] max-w-[80vw] rounded-lg border border-gray-200 bg-white shadow-xl p-2">
          <div className="flex items-center justify-between gap-2 pb-2 mb-2 border-b">
            <button
              type="button"
              onClick={() => onChange(allSelected ? "" : available.join(","))}
              disabled={!available.length}
              className="text-xs font-semibold text-blue-700 hover:underline disabled:text-gray-400"
            >
              {allSelected ? "Clear All" : `Select All (${available.length})`}
            </button>
            {selected.length > 0 && (
              <button type="button" onClick={() => onChange("")} className="text-xs text-gray-600 hover:underline">
                Clear
              </button>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1">
            {!displayOptions.length ? (
              <p className="p-2 text-xs text-amber-700">No classifications found for this sport.</p>
            ) : (
              displayOptions.map((classification) => (
                <label
                  key={classification}
                  className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs cursor-pointer ${
                    selectedSet.has(classification) ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(classification)}
                    onChange={() => toggle(classification)}
                  />
                  <span>{classification}</span>
                  {!available.includes(classification) && (
                    <span className="ml-auto text-[10px] text-amber-700">Legacy value</span>
                  )}
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </details>
  );
};

const EligibilityManager = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("database");
  const [query, setQuery] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);
  const [sports, setSports] = useState([]);
  const [classificationsBySport, setClassificationsBySport] = useState({});

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await API.fetchEligibilityRules();
      const list = Array.isArray(res?.data) ? res.data : [];
      setRules(
        list.map((r, i) => ({
          id: r?.id ?? `tmp_loaded_${i}`,
          gender: normalizeGender(r?.gender) || "male",
          age_category: normalizeAgeCategory(r?.age_category) || "sub_junior",
          sport: String(r?.sport || ""),
          event: String(r?.event || ""),
          eligible_classes: String(r?.eligible_classes || ""),
        }))
      );
      setSource(String(res?.source || "database"));
    } catch (err) {
      errorAlert("Load failed", err?.message || "Unable to load eligibility rules.");
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  useEffect(() => {
    const loadEligibilityOptions = async () => {
      try {
        const sportRows = await API.getSports();
        const list = (Array.isArray(sportRows) ? sportRows : sportRows?.data || [])
          .filter((sport) => sport?.id && String(sport?.name || "").trim())
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setSports(list);

        const results = await Promise.all(
          list.map(async (sport) => {
            try {
              const response = await API.get(
                `/admin/classifications/sports/${sport.id}/classifications`
              );
              const rows = Array.isArray(response) ? response : response?.data || [];
              return [
                String(sport.id),
                Array.from(
                  new Set(rows.map((row) => String(row?.classification || "").trim()).filter(Boolean))
                ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
              ];
            } catch (_err) {
              return [String(sport.id), []];
            }
          })
        );
        setClassificationsBySport(Object.fromEntries(results));
      } catch (err) {
        errorAlert("Options unavailable", err?.message || "Unable to load sports and classifications.");
        setSports([]);
        setClassificationsBySport({});
      }
    };

    loadEligibilityOptions();
  }, []);

  const sportIdForName = (sportName) =>
    sports.find(
      (sport) => String(sport.name || "").trim().toLowerCase() === String(sportName || "").trim().toLowerCase()
    )?.id;

  const classificationOptionsForSport = (sportName) => {
    const sportId = sportIdForName(sportName);
    return sportId ? classificationsBySport[String(sportId)] || [] : [];
  };

  const validateRow = (row) =>
    !normalizeGender(row.gender) ||
    !normalizeAgeCategory(row.age_category) ||
    !String(row.sport || "").trim() ||
    !String(row.event || "").trim() ||
    !String(row.eligible_classes || "").trim();

  const rowsWithIdx = useMemo(
    () => rules.map((r, i) => ({ ...r, _idx: i, _invalid: validateRow(r) })),
    [rules]
  );

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rowsWithIdx.filter((row) => {
      if (genderFilter !== "all" && normalizeGender(row.gender) !== genderFilter) return false;
      if (ageFilter !== "all" && normalizeAgeCategory(row.age_category) !== ageFilter) return false;
      if (showOnlyInvalid && !row._invalid) return false;
      if (!q) return true;
      const hay = `${row.sport} ${row.event} ${row.eligible_classes}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rowsWithIdx, query, genderFilter, ageFilter, showOnlyInvalid]);

  const invalidCount = useMemo(() => rowsWithIdx.filter((r) => r._invalid).length, [rowsWithIdx]);

  const updateRule = (idx, field, value) => {
    setRules((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const deleteRule = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const addRule = () => {
    const newRule = createEmptyRule();

    // Put new rules where the user can see them immediately. Previously they
    // were appended after the complete ruleset and could also be hidden by an
    // active search/filter, which made the button appear to do nothing.
    setQuery("");
    setGenderFilter("all");
    setAgeFilter("all");
    setShowOnlyInvalid(false);
    setRules((prev) => [newRule, ...prev]);

    window.setTimeout(() => {
      const input = document.getElementById(`eligibility-sport-${newRule.id}__search`);
      input?.scrollIntoView({ behavior: "smooth", block: "center" });
      input?.focus();
    }, 0);
  };

  const normalizeForSave = (row) => ({
    gender: normalizeGender(row.gender),
    age_category: normalizeAgeCategory(row.age_category),
    sport: String(row.sport || "").trim(),
    event: String(row.event || "").trim(),
    eligible_classes: String(row.eligible_classes || "").trim(),
  });

  const saveRules = async () => {
    if (!rules.length) {
      warningAlert("No rows", "Add at least one rule before saving.");
      return;
    }

    const normalized = rules.map(normalizeForSave);
    const missingIdx = normalized.findIndex(
      (r) => !r.gender || !r.age_category || !r.sport || !r.event || !r.eligible_classes
    );
    if (missingIdx >= 0) {
      warningAlert("Validation", `Row ${missingIdx + 1} has missing required fields.`);
      return;
    }

    const seen = new Set();
    for (let i = 0; i < normalized.length; i += 1) {
      const r = normalized[i];
      const key = `${r.gender}|${r.age_category}|${r.sport.toLowerCase()}|${r.event.toLowerCase()}`;
      if (seen.has(key)) {
        warningAlert(
          "Validation",
          `Duplicate rule at row ${i + 1} with same Gender + Age + Sport + Event.`
        );
        return;
      }
      seen.add(key);
    }

    setSaving(true);
    try {
      const res = await API.saveEligibilityRules(normalized);
      successAlert("Saved", res?.message || "Eligibility rules updated.");
      await loadRules();
    } catch (err) {
      errorAlert("Save failed", err?.message || "Unable to save eligibility rules.");
    } finally {
      setSaving(false);
    }
  };

  const resetFromConfig = async () => {
    try {
      const res = await API.resetEligibilityRules();
      successAlert("Reloaded", res?.message || "Rules reloaded from config.");
      await loadRules();
    } catch (err) {
      errorAlert("Reset failed", err?.message || "Unable to reload from config.");
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Eligibility Management</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage game eligibility by gender, age category, sport, and event.
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Source: {source === "database" ? "Database" : "Config fallback"} | Total: {rules.length} | Invalid:{" "}
                {invalidCount}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate("/admin")}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm"
              >
                Back to Dashboard
              </button>
              <button
                onClick={resetFromConfig}
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm"
              >
                Reload From Config
              </button>
              <button
                type="button"
                onClick={addRule}
                disabled={loading || saving}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Add Rule
              </button>
              <button
                onClick={saveRules}
                disabled={saving || loading}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:bg-gray-400"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-4 mb-4">
            <div className="grid md:grid-cols-5 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Search</label>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Sport / event / classes"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Gender</label>
                <SearchableSelect
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">All</option>
                  {GENDER_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Age Category</label>
                <SearchableSelect
                  value={ageFilter}
                  onChange={(e) => setAgeFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">All</option>
                  {AGE_OPTIONS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showOnlyInvalid}
                    onChange={(e) => setShowOnlyInvalid(e.target.checked)}
                  />
                  Show only invalid rows
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 border">#</th>
                    <th className="p-2 border">Gender</th>
                    <th className="p-2 border">Age Category</th>
                    <th className="p-2 border">Sport</th>
                    <th className="p-2 border">Event</th>
                    <th className="p-2 border">Eligible Classes</th>
                    <th className="p-2 border">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && !filteredRows.length ? (
                    <tr>
                      <td className="p-3 text-center text-gray-500 border" colSpan={7}>
                        No rules found for current filters.
                      </td>
                    </tr>
                  ) : null}
                  {filteredRows.map((row, i) => (
                    <tr key={`${row.id}_${row._idx}`} className={row._invalid ? "bg-rose-50" : ""}>
                      <td className="p-2 border align-top">{i + 1}</td>
                      <td className="p-2 border align-top min-w-[120px]">
                        <SearchableSelect
                          value={row.gender}
                          onChange={(e) => updateRule(row._idx, "gender", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        >
                          {GENDER_OPTIONS.map((g) => (
                            <option key={g.value} value={g.value}>
                              {g.label}
                            </option>
                          ))}
                        </SearchableSelect>
                      </td>
                      <td className="p-2 border align-top min-w-[140px]">
                        <SearchableSelect
                          value={row.age_category}
                          onChange={(e) => updateRule(row._idx, "age_category", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        >
                          {AGE_OPTIONS.map((a) => (
                            <option key={a.value} value={a.value}>
                              {a.label}
                            </option>
                          ))}
                        </SearchableSelect>
                      </td>
                      <td className="p-2 border align-top min-w-[180px]">
                        <SearchableSelect
                          id={`eligibility-sport-${row.id}`}
                          value={row.sport}
                          onChange={(e) => {
                            updateRule(row._idx, "sport", e.target.value);
                            updateRule(row._idx, "eligible_classes", "");
                          }}
                          placeholder="Select sport"
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">Select sport</option>
                          {sports.map((sport) => (
                            <option key={sport.id} value={sport.name}>
                              {sport.name}
                            </option>
                          ))}
                        </SearchableSelect>
                      </td>
                      <td className="p-2 border align-top min-w-[180px]">
                        <input
                          type="text"
                          value={row.event}
                          onChange={(e) => updateRule(row._idx, "event", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="p-2 border align-top min-w-[260px]">
                        <ClassificationMultiSelect
                          options={classificationOptionsForSport(row.sport)}
                          value={row.eligible_classes}
                          onChange={(value) => updateRule(row._idx, "eligible_classes", value)}
                          disabled={!sportIdForName(row.sport)}
                        />
                      </td>
                      <td className="p-2 border align-top min-w-[90px]">
                        <button
                          onClick={() => deleteRule(row._idx)}
                          className="px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 text-xs"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EligibilityManager;
